import React, { useState, useEffect, useRef } from "react";

const GREEN = "rgba(0, 170, 0, 1)";

// Updated clean vector icons
const icons = {
  steps: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <path d="M7 9c1.5-2 3.5-2 5 0M7 9C4 13 4 15 7 17s4 2 5-1M7 9c0-2 2-4 4-4" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
      <path d="M15 10c1.5-2 3.5-2 5 0M15 10c-3 4-3 6 0 8s4 2 5-1M15 10c0-2 2-4 4-4" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  turn: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <path d="M12 3v7h4l-6 9v-7H6z" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  elevator: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" stroke={GREEN} strokeWidth="2" />
      <path d="M8 14v-4M10 10l-2-2-2 2" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
      <path d="M16 10v4M14 14l2 2 2-2" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  fix: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <path d="M12 5a7 7 0 11-7 7M5 5v5h5" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  destination: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <path d="M12 21l5-9a5 5 0 10-10 0l5 9z" stroke={GREEN} strokeWidth="2" />
      <circle cx="12" cy="12" r="2" fill={GREEN} />
    </svg>
  ),
  save: (
    <svg width="68" height="68" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="4" width="14" height="16" rx="2" stroke={GREEN} strokeWidth="2" />
      <path d="M9 4v5h6V4" stroke={GREEN} strokeWidth="2" />
      <path d="M12 14v4" stroke={GREEN} strokeWidth="2" />
    </svg>
  )
};

const slides = [
  {
    icon: icons.steps,
    title: "Walk Straight",
    text: "Walk normally. We count your steps and convert them into distances automatically.\n\nKeep your phone steady in hand or pocket — best accuracy!"
  },
  {
    icon: icons.turn,
    title: "Tap When You Turn",
    text: "Tap the direction button exactly when you turn.\n\nEven small hallway bends matter indoors.\n\nEvery tap = a new segment in your map."
  },
  {
    icon: icons.elevator,
    title: "Using Elevators",
    text: "Tap Elevator before entering — step counting pauses.\n\nWhen you exit, simply tap the next direction.\n\nThis keeps floor changes accurate!"
  },
//   {
//     icon: icons.fix,
//     title: "Fixing Mistakes",
//     text: "Forgot to tap? No stress!\nYou can:\n• Tap when you remember (it still logs correctly)\n• Walk a few steps back and re-tap\n\nNever restart the whole route! 😌"
//   },
  {
    icon: icons.destination,
    title: "End the Route",
    text: "Tap Destination at the final point.\n\nWe stop tracking and prepare your results to review."
  },
  {
    icon: icons.save,
    title: "Save & Share",
    text: "Give your route a name — you'll get a link.\n\nUse it later or share with someone who needs it.\n\nSmarter indoor navigation for everyone!"
  }
];

export default function GoKeiroOnboarding() {
  const [page, setPage] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const total = slides.length;

  // Measure real container width
  useEffect(() => {
    const updateWidth = () => {
      if (!containerRef.current) return;
      setSlideWidth(containerRef.current.offsetWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Move slides with real width
  useEffect(() => {
    if (!trackRef.current || !slideWidth) return;
    trackRef.current.style.transform = `translateX(-${page * slideWidth}px)`;
  }, [page, slideWidth]);

  const finish = () => {
    localStorage.setItem("onboardingSeen", "true");
    window.location.reload();

  };

  const handleSwipeEnd = () => {
    const delta = touchEndX.current - touchStartX.current;
    const threshold = 50;

    if (Math.abs(delta) < threshold) return;
    if (delta < 0 && page < total - 1) setPage(page + 1);
    if (delta > 0 && page > 0) setPage(page - 1);
  };

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.header}>How goKeiro Works</h2>
      <p style={styles.subHeader}>Walk  •  Tap  •  Save your routes</p>

      <div
        ref={containerRef}
        style={styles.sliderContainer}
        onTouchStart={(e) => (touchStartX.current = e.touches[0].screenX)}
        onTouchEnd={(e) => {
          touchEndX.current = e.changedTouches[0].screenX;
          handleSwipeEnd();
        }}
      >
        <div
          ref={trackRef}
          style={{
            ...styles.sliderTrack,
            width: slideWidth ? slideWidth * total : "100%"
          }}
        >
          {slides.map((s, i) => (
            <div
              key={i}
              style={{
                ...styles.slide,
                flex: `0 0 ${slideWidth}px`,
                maxWidth: slideWidth
              }}
            >
              <div style={styles.icon}>{s.icon}</div>
              <h3 style={styles.title}>{s.title}</h3>
              <p style={styles.text}>{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.progressLabel}>
          Slide {page + 1} of {total}
        </div>

        <div style={styles.dots}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{ ...styles.dot, ...(i === page ? styles.activeDot : {}) }}
              onClick={() => setPage(i)}
            />
          ))}
        </div>

        <div style={styles.btnRow}>
          <button
            style={{
              ...styles.btn,
              ...(page === 0 && { opacity: 0.3, pointerEvents: "none" })
            }}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>

          {page < total - 1 ? (
            <button style={styles.primaryBtn} onClick={() => setPage(page + 1)}>
              Next
            </button>
          ) : (
            <button style={styles.primaryBtn} onClick={finish}>
              Let’s Go!
            </button>
          )}
        </div>

        {/* <button style={styles.skip} onClick={finish}>
          Skip Onboarding
        </button> */}
      </div>
    </div>
  );
}

// ---- Styles ----
const styles = {
  wrapper: {
    width: "100vw",
    height: "100dvh",
    overflow: "hidden",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  header: {
    fontSize: 21,
    fontWeight: 700,
    textAlign: "center",
    color: "#111",
    marginTop: 20,
  },
  subHeader: {
    fontSize: 15,
    color: "#333",
    textAlign: "center",
    marginBottom: 10,
  },
  sliderContainer: {
    flex: 1,
    width: "100%",
    overflow: "hidden",
  },
  sliderTrack: {
    display: "flex",
    height: "100%",
    transition: "transform .35s ease",
  },
  slide: {
    height: "100%",
    padding: "0 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop : '50px',
    // justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
  },
  icon: {
    marginBottom: 18,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111",
    marginBottom: 10,
  },
  text: {
    fontSize: 16,
    color: "#444",
    lineHeight: 1.45,
    maxWidth: 340,
    whiteSpace: "pre-line",
  },
  footer: {
    position: "absolute",
    bottom: 14,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  progressLabel: {
    fontSize: 12,
    marginBottom: 12,
    color: "#333",
  },
  dots: {
    display: "flex",
    gap: 6,
    marginBottom: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 20,
    background: "#BBB",
  },
  activeDot: {
    width: 20,
    background: GREEN,
  },
  btnRow: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
  },
  btn: {
    padding: "10px 17px",
    borderRadius: 50,
    fontSize: 14,
    cursor: "pointer",
    background: "#FFF",
    color: "#111",
    border: `1.6px solid ${GREEN}`,
  },
  primaryBtn: {
    padding: "10px 17px",
    borderRadius: 50,
    fontSize: 14,
    cursor: "pointer",
    background: "#FFF",
    border: `1.6px solid ${GREEN}`,
    color: "#111",
    fontWeight: 700,
  },
  skip: {
    marginTop: 4,
    background: "transparent",
    border: "none",
    fontSize: 12,
    color: "#444",
    textDecoration: "underline",
    cursor: "pointer",
  },
};
